import { CalendarRange, MapPin, Stethoscope } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import type { ListRequests } from '@cold-ambulance/client';
import { Skeleton, buttonVariants, cn } from '@cold-ambulance/shadcn';
import { formatDate, sumEquipmentQuantities } from '@cold-ambulance/ui';

export function RequestCardSkeleton() {
  return (
    <div className="flex items-center justify-between rounded-lg border bg-white p-8">
      <div className="space-y-5">
        <div className="flex items-center gap-2">
          <Skeleton className="h-5 w-5 rounded-sm" />
          <Skeleton className="h-4 w-40" />
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Skeleton className="h-5 w-5 rounded-sm" />
          <Skeleton className="h-4 w-12" />
          <Skeleton className="size-2" />
          <Skeleton className="h-4 w-12" />
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Skeleton className="h-5 w-5 rounded-sm" />
          <Skeleton className="h-4 w-12" />
          <span className="flex items-center gap-0.5 rounded-md border bg-white">
            <Skeleton className="h-3 w-8" />
          </span>
          <span className="flex items-center gap-0.5 rounded-md border bg-white">
            <Skeleton className="h-3 w-8" />
          </span>
        </div>
      </div>
      <Skeleton className="h-11 w-28 rounded-md" />
    </div>
  );
}

export function UpdatedRequestCard({
  request,
}: {
  request: ListRequests['records'][number];
}) {
  const { t } = useTranslation();

  const ambulanceCount =
    request.ambulanceServiceWithDriver.reduce(
      (acc, service) =>
        acc + service.type1Amount + service.type2Amount + service.type3Amount,
      0,
    ) +
    request.ambulanceServiceWithoutDriver.reduce(
      (acc, service) =>
        acc + service.type1Amount + service.type2Amount + service.type3Amount,
      0,
    ) +
    request.ambulance4x4WithDriver.reduce(
      (acc, service) => acc + service.amount,
      0,
    ) +
    request.ambulance4x4WithoutDriver.reduce(
      (acc, service) => acc + service.amount,
      0,
    );
  const mobileClinicCount = request.mobileClinicService.reduce(
    (acc, service) => acc + service.amount,
    0,
  );
  const paramedicCount = request.requestParamedicService.reduce(
    (acc, service) => acc + service.amount,
    0,
  );
  const nurseCount = request.requestNurseService.reduce(
    (acc, service) => acc + service.amount,
    0,
  );
  const doctorCount = request.doctorService.reduce(
    (acc, service) => acc + service.amount,
    0,
  );

  return (
    <div className="flex flex-col gap-8 rounded-lg border bg-white p-8">
      <div className="space-y-5">
        <div className="flex items-center gap-2">
          <MapPin size={20} />
          <p className="text-[18px] font-bold">{request.projectName}</p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <CalendarRange size={20} />
          {formatDate(request.scheduleStartDate)} -{' '}
          {formatDate(request.scheduleEndDate)}
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Stethoscope size={20} />
          {ambulanceCount > 0 && (
            <>
              {ambulanceCount}
              <span>{t('services.ambulance')}</span>
            </>
          )}
          {(() => {
            const equipmentCount =
              request.ambulanceServiceWithDriver.reduce(
                (acc, service) =>
                  acc +
                  sumEquipmentQuantities(service.type1Equipment) +
                  sumEquipmentQuantities(service.type2Equipment) +
                  sumEquipmentQuantities(service.type3Equipment),
                0,
              ) +
              request.ambulanceServiceWithoutDriver.reduce(
                (acc, service) =>
                  acc +
                  sumEquipmentQuantities(service.type1Equipment) +
                  sumEquipmentQuantities(service.type2Equipment) +
                  sumEquipmentQuantities(service.type3Equipment),
                0,
              ) +
              request.ambulance4x4WithDriver.reduce(
                (acc, service) => acc + sumEquipmentQuantities(service.equipment),
                0,
              ) +
              request.ambulance4x4WithoutDriver.reduce(
                (acc, service) => acc + sumEquipmentQuantities(service.equipment),
                0,
              ) +
              request.mobileClinicService.reduce(
                (acc, service) => acc + sumEquipmentQuantities(service.equipment),
                0,
              ) +
              sumEquipmentQuantities(request.medicalEquipment);
            return equipmentCount > 0 ? (
              <span className="text-muted-foreground text-xs">
                ({equipmentCount} {t('services.equipment')})
              </span>
            ) : null;
          })()}
          {mobileClinicCount > 0 && (
            <span className="flex items-center gap-0.5 rounded-md border bg-white px-1 py-px">
              {mobileClinicCount}
              <span>{t('services.mobileClinic')}</span>
            </span>
          )}
          {paramedicCount > 0 && (
            <span className="flex items-center gap-0.5 rounded-md border bg-white px-1 py-px">
              {paramedicCount}
              <span>{t('services.paramedic')}</span>
            </span>
          )}
          {nurseCount > 0 && (
            <span className="flex items-center gap-0.5 rounded-md border bg-white px-1 py-px">
              {nurseCount}
              <span>{t('services.nurse')}</span>
            </span>
          )}
          {doctorCount > 0 && (
            <span className="flex items-center gap-0.5 rounded-md border bg-white px-1 py-px">
              {doctorCount}
              <span>{t('services.doctor')}</span>
            </span>
          )}
        </div>
      </div>
      <Link
        to={`/offers/place/${request.id}`}
        className={buttonVariants({
          className: cn('h-11! px-6'),
        })}
      >
        <p className="text-[14px] font-bold">{t('requests.requestDetails')}</p>
      </Link>
    </div>
  );
}
